import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import EmblaCarousel from 'embla-carousel';
import Fade from 'embla-carousel-fade';
import Autoplay from 'embla-carousel-autoplay';
import PhotoSwipe from 'photoswipe/dist/photoswipe.esm.js';
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
import { addDotBtnsAndClickHandlers } from './EmblaCarouselDotButton';

gsap.registerPlugin(ScrollTrigger);

// --- Booking-kalender (Alpine-komponent) ---
// Registreres på alpine:init — app.js lastes før Alpine i layouten.
const BOOKING_WEEKDAYS = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };

document.addEventListener('alpine:init', () => {
    window.Alpine.data('bookingCalendar', () => ({
        open: false,
        data: { currency: 'NOK', years: [] },
        cursor: null,     // 'YYYY-MM' for første synlige måned
        monthCount: 2,
        months: [],
        arrival: null,    // 'YYYY-MM-DD'
        departure: null,
        hover: null,
        step: 1,          // 1 = kalender, 2 = kontaktdetaljer, 3 = kvittering
        contact: { navn: '', epost: '', telefon: '', melding: '' },
        submitting: false,
        errors: {},
        errorMessage: null,
        captchas: [],     // [{ name, value }] fra Formies refresh-tokens

        init() {
            const node = this.$el.querySelector('[data-booking-data]');
            if (node) this.data = JSON.parse(node.textContent);
            this.cursor = this.todayIso().slice(0, 7);
            const mq = window.matchMedia('(min-width: 768px)');
            this.monthCount = mq.matches ? 2 : 1;
            mq.addEventListener('change', (e) => { this.monthCount = e.matches ? 2 : 1; this.build(); });
            this.$watch('open', (v) => document.documentElement.classList.toggle('overflow-hidden', v));
            this.build();
        },

        // Datohjelpere — lokal tid, ISO-strenger for all sammenlikning
        todayIso() { return this.iso(new Date()); },
        iso(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); },
        parse(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); },
        addDays(s, n) { const d = this.parse(s); d.setDate(d.getDate() + n); return this.iso(d); },
        dow(s) { return this.parse(s).getDay(); },
        diffDays(a, b) { return Math.round((this.parse(b) - this.parse(a)) / 86400000); },

        // Regler og pris
        yearBlock(s) { return this.data.years.find((y) => String(y.year) === s.slice(0, 4)) || null; },
        periodFor(s) {
            const yb = this.yearBlock(s);
            if (!yb) return null;
            const ex = (yb.exceptions || []).find((e) => e.from <= s && s <= e.to);
            return {
                dayPrice: ex && ex.dayPrice != null ? ex.dayPrice : yb.dayPrice,
                weekEndDayPrice: ex && ex.weekEndDayPrice != null ? ex.weekEndDayPrice : yb.weekEndDayPrice,
                minDayRental: (ex && ex.minDayRental != null ? ex.minDayRental : yb.minDayRental) || 1,
                startDay: ex ? (ex.startDay || null) : (yb.startDay || null),
            };
        },
        isBlockedNight(s) {
            const yb = this.yearBlock(s);
            if (!yb) return true;
            // Utsjekksdagen (b.to) er ledig som ny ankomstdag — siste blokkerte natt er dagen før.
            return (yb.booked || []).some((b) => b.from <= s && s < b.to);
        },
        nightPrice(s) {
            const p = this.periodFor(s);
            if (!p || p.dayPrice == null) return null;
            const wd = this.dow(s);
            const weekend = wd === 5 || wd === 6; // fredags- og lørdagsnatt
            return weekend && p.weekEndDayPrice != null ? p.weekEndDayPrice : p.dayPrice;
        },
        nightOk(s) { return !this.isBlockedNight(s) && this.nightPrice(s) != null; },

        canArrive(s) {
            if (s < this.todayIso()) return false;
            const p = this.periodFor(s);
            if (!p) return false;
            if (p.startDay && this.dow(s) !== BOOKING_WEEKDAYS[p.startDay]) return false;
            for (let i = 0; i < p.minDayRental; i++) {
                if (!this.nightOk(this.addDays(s, i))) return false;
            }
            return true;
        },
        // Ankomstdatoens periode styrer min. netter og ukesrytme (startDay)
        canDepart(s) {
            if (!this.arrival || s <= this.arrival) return false;
            const p = this.periodFor(this.arrival);
            if (this.diffDays(this.arrival, s) < p.minDayRental) return false;
            if (p.startDay && this.dow(s) !== BOOKING_WEEKDAYS[p.startDay]) return false;
            for (let d = this.arrival; d < s; d = this.addDays(d, 1)) {
                if (!this.nightOk(d)) return false;
            }
            return true;
        },

        // Uke-modus: i perioder med startdag snappes valget til hele uker.
        // Klikk på en hvilken som helst dag velger uka den tilhører;
        // klikk på en senere uke forlenger oppholdet.
        weekInfo(s) {
            const p = this.periodFor(s);
            if (!p || !p.startDay) return null;
            const back = (this.dow(s) - BOOKING_WEEKDAYS[p.startDay] + 7) % 7;
            const start = this.addDays(s, -back);
            const sp = this.periodFor(start);
            if (!sp || sp.startDay !== p.startDay) return null; // uka starter utenfor perioden
            return { start, end: this.addDays(start, sp.minDayRental || 7) };
        },

        // Interaksjon — to klikk også i uke-modus: første klikk snapper til
        // ukestarten (ankomst), andre klikk velger avreisedag (neste gyldige
        // startdag — én eller flere uker senere).
        // I avreisefasen snappes valget til minste gyldige opphold:
        // en dag i en senere uke betyr «avreise etter den uka» (wk.end),
        // og en dag innenfor min-grensa betyr «avreise etter minsteoppholdet».
        snapDeparture(s) {
            if (!this.arrival || this.departure) return null;
            if (this.canDepart(s)) return s; // direkte på gyldig avreisedag
            const wk = this.weekInfo(s);
            if (wk && wk.end > this.arrival && this.canDepart(wk.end)) return wk.end;
            const p = this.periodFor(this.arrival);
            if (!p.startDay && s > this.arrival) {
                const minDep = this.addDays(this.arrival, p.minDayRental || 1);
                if (s < minDep && this.canDepart(minDep)) return minDep;
            }
            return null;
        },
        pick(s) {
            const dep = this.snapDeparture(s);
            if (dep) { this.departure = dep; return; }
            const wk = this.weekInfo(s);
            if (wk) {
                if (this.canArrive(wk.start)) { this.arrival = wk.start; this.departure = null; }
                return;
            }
            if (this.canArrive(s)) { this.arrival = s; this.departure = null; }
        },
        isPickable(s) {
            if (!s) return false;
            if (this.snapDeparture(s)) return true;
            const wk = this.weekInfo(s);
            if (wk) return this.canArrive(wk.start);
            return this.canArrive(s);
        },
        isEnd(s) { return s && (s === this.arrival || s === this.departure); },
        previewRange() {
            if (!this.hover) return null;
            const dep = this.snapDeparture(this.hover);
            if (dep) return { a: this.arrival, b: dep };
            const wk = this.weekInfo(this.hover);
            if (wk) return this.canArrive(wk.start) ? { a: wk.start, b: wk.end } : null;
            // ankomstfase: forhåndsvis minsteoppholdet fra dagen man peker på
            if (this.canArrive(this.hover)) {
                const p = this.periodFor(this.hover);
                return { a: this.hover, b: this.addDays(this.hover, p.minDayRental || 1) };
            }
            return null;
        },
        // Bakgrunnsklasser for cellen: range-flate med halvpille i endene
        rangeClass(s) {
            if (!s) return '';
            let cls = '';
            const apply = (r) => {
                if (!r) return;
                if (s === r.a) cls = 'bg-linen rounded-l-full';
                else if (s === r.b) cls = 'bg-linen rounded-r-full';
                else if (s > r.a && s < r.b) cls = 'bg-linen';
            };
            apply(this.previewRange());
            if (this.arrival && this.departure) apply({ a: this.arrival, b: this.departure });
            return cls;
        },
        nights() { return this.arrival && this.departure ? this.diffDays(this.arrival, this.departure) : 0; },
        totalPrice() {
            let sum = 0;
            for (let d = this.arrival; d < this.departure; d = this.addDays(d, 1)) sum += this.nightPrice(d) || 0;
            return sum;
        },

        // Captcha-tokens: slideouten er ikke et Formie-rendret skjema, så de
        // skjulte captcha-feltene finnes ikke i markupen. Uten dem regnes hver
        // ekte forespørsel som spam. Vi henter dem fra Formies eget
        // refresh-tokens-endepunkt (samme kilde som Formies ajax-skjemaer
        // bruker), og får oppdatert CSRF-token på kjøpet.
        async fetchTokens() {
            if (!this.data.refreshUrl || !this.data.formHandle) return;
            try {
                const url = `${this.data.refreshUrl}${this.data.refreshUrl.includes('?') ? '&' : '?'}form=${encodeURIComponent(this.data.formHandle)}`;
                const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
                const json = await res.json();
                if (json?.csrf?.token) this.data.csrfToken = json.csrf.token;
                // { handle: { sessionKey, value } } — honeypot har ingen verdi
                // og MÅ sendes tom, ellers avvises innsendingen.
                this.captchas = Object.values(json?.captchas || {})
                    .filter((c) => c && c.sessionKey)
                    .map((c) => ({ name: c.sessionKey, value: c.value ?? '' }));
            } catch (e) {
                // Nettverksfeil her skal ikke blokkere skjemaet — da sendes
                // forespørselen uten tokens og fanges eventuelt som spam.
                this.captchas = [];
            }
        },

        // Innsending — Formie tar imot, serveren regner prisen på nytt
        async submitRequest() {
            if (this.submitting || !this.data.formHandle) return;
            this.submitting = true;
            this.errors = {};
            this.errorMessage = null;
            try {
                const fd = new FormData();
                fd.append(this.data.csrfName, this.data.csrfToken);
                fd.append('action', 'formie/submissions/submit');
                fd.append('handle', this.data.formHandle);
                fd.append('fields[navn]', this.contact.navn);
                fd.append('fields[epost]', this.contact.epost);
                fd.append('fields[telefon]', this.contact.telefon);
                fd.append('fields[melding]', this.contact.melding);
                fd.append('fields[bolig]', this.data.unitTitle);
                fd.append('fields[boligId]', this.data.unitId);
                fd.append('fields[ankomst]', this.arrival);
                fd.append('fields[avreise]', this.departure);
                fd.append('fields[netter]', this.nights());
                fd.append('fields[pris]', this.totalPrice());
                this.captchas.forEach((c) => fd.append(c.name, c.value));
                const res = await fetch(window.location.pathname, {
                    method: 'POST',
                    body: fd,
                    headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                });
                const json = await res.json();
                if (json && (json.success === true || json.submissionId)) {
                    this.step = 3;
                } else {
                    const errors = (json && json.errors) || {};
                    // Formie gir feil som { handle: ['melding'] } — flat ut til strenger
                    this.errors = Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]));
                    this.errorMessage = (json && json.errorMessage) || (Object.keys(this.errors).length ? null : 'Noe gikk galt. Prøv igjen.');
                }
            } catch (e) {
                this.errorMessage = 'Noe gikk galt. Prøv igjen.';
            }
            this.submitting = false;
        },

        // Visning
        openPanel() {
            if (this.step === 3) {
                // Ny forespørsel etter fullført innsending
                this.step = 1;
                this.arrival = null;
                this.departure = null;
                this.contact = { navn: '', epost: '', telefon: '', melding: '' };
                this.errors = {};
                this.errorMessage = null;
            }
            this.open = true;
            // Hentes ved åpning, ikke ved innsending: Javascript-captchaen
            // måler tiden fra tokenet ble laget til skjemaet sendes.
            this.fetchTokens();
        },
        build() {
            const [y, m] = this.cursor.split('-').map(Number);
            const fmt = new Intl.DateTimeFormat('nb-NO', { month: 'long', year: 'numeric' });
            this.months = Array.from({ length: this.monthCount }, (_, i) => {
                const first = new Date(y, m - 1 + i, 1);
                const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
                const lead = (first.getDay() + 6) % 7; // mandag først
                const cells = [];
                for (let b = 0; b < lead; b++) cells.push({ key: 'blank' + b, day: null, iso: null });
                for (let dd = 1; dd <= daysInMonth; dd++) {
                    const iso = this.iso(new Date(first.getFullYear(), first.getMonth(), dd));
                    cells.push({ key: iso, day: dd, iso });
                }
                return { key: this.iso(first).slice(0, 7), label: fmt.format(first), cells };
            });
        },
        canPrev() { return this.cursor > this.todayIso().slice(0, 7); },
        prevMonth() { if (this.canPrev()) this.shiftCursor(-1); },
        nextMonth() { this.shiftCursor(1); },
        shiftCursor(n) {
            const [y, m] = this.cursor.split('-').map(Number);
            this.cursor = this.iso(new Date(y, m - 1 + n, 1)).slice(0, 7);
            this.build();
        },

        phaseHint() {
            if (!this.arrival) return 'Velg ankomstdato';
            if (!this.departure) {
                const p = this.periodFor(this.arrival);
                return this.weekInfo(this.arrival)
                    ? 'Velg avreise · hele uker'
                    : 'Velg avreise · minst ' + p.minDayRental + ' netter';
            }
            return 'Velg ny ankomstdato for å endre';
        },
        footerHint() {
            return this.arrival
                ? 'Velg avreisedato for å se totalpris.'
                : 'Velg ankomstdato i kalenderen for å se pris og tilgjengelighet.';
        },
        fmtDate(s) { return new Intl.DateTimeFormat('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' }).format(this.parse(s)); },
        fmtPrice(n) { return 'kr ' + n.toLocaleString('nb-NO'); },
    }));
});

document.addEventListener('DOMContentLoaded', () => {

    // Scroll-down fade-out
    if (document.querySelector('.scroll-down') && document.querySelector('#top')) {
        gsap.to(".scroll-down", {
            scrollTrigger: {
                trigger: "#top",
                start: "60px top",
                //markers: true
            },
            opacity: 0
        });
    }

    // Hero-slideshow — fade + autoplay (forsiden og hero-blokker i Main Content)
    document.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
        const viewport = carousel.querySelector('[data-hero-slideshow]');
        if (!viewport) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const plugins = [Fade()];
        if (!prefersReducedMotion) {
            plugins.push(Autoplay({ delay: 6000, stopOnInteraction: false }));
        }
        const heroEmbla = EmblaCarousel(viewport, { loop: true, duration: 40 }, plugins);

        const dotsNode = carousel.querySelector('[data-hero-dots]');
        if (dotsNode) addDotBtnsAndClickHandlers(heroEmbla, dotsNode);
    });

    // Header: auto-hide — skjules ved scroll ned, vises ved scroll opp
    const siteHeader = document.querySelector('[data-site-header]');
    if (siteHeader) {
        let lastY = window.scrollY;
        let ticking = false;
        const JITTER = 8; // ignorer bittesmå bevegelser

        const updateHeader = () => {
            ticking = false;
            const y = window.scrollY;
            const delta = y - lastY;
            if (Math.abs(delta) < JITTER) return;

            // Ikke skjul mens mobilmenyen er åpen
            const mobileMenu = siteHeader.querySelector('[x-show="openMenu"]');
            const menuOpen = mobileMenu && getComputedStyle(mobileMenu).display !== 'none';

            if (delta > 0 && y > 120 && !menuOpen) {
                siteHeader.classList.add('is-hidden');
            } else if (delta < 0 || y <= 120) {
                siteHeader.classList.remove('is-hidden');
            }
            lastY = y;
        };

        window.addEventListener('scroll', () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(updateHeader);
            }
        }, { passive: true });
    }

    // Åpne booking-slideouten fra alle [data-open-booking]-knapper
    document.querySelectorAll('[data-open-booking]').forEach((btn) => {
        btn.addEventListener('click', () => window.dispatchEvent(new CustomEvent('open-booking')));
    });

    // Beliggenhetskart (rental unit) — mapbox-gl lastes fra CDN kun ved behov
    const loadMapboxGl = () => new Promise((resolve, reject) => {
        if (window.mapboxgl) { resolve(window.mapboxgl); return; }
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css';
        document.head.appendChild(css);
        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js';
        script.onload = () => resolve(window.mapboxgl);
        script.onerror = reject;
        document.body.appendChild(script);
    });

    const unitMapNode = document.querySelector('[data-unit-map]');
    if (unitMapNode && unitMapNode.dataset.token) {
        loadMapboxGl().then((mapboxgl) => {
            mapboxgl.accessToken = unitMapNode.dataset.token;
            const center = [parseFloat(unitMapNode.dataset.lng), parseFloat(unitMapNode.dataset.lat)];
            const map = new mapboxgl.Map({
                container: unitMapNode,
                style: 'mapbox://styles/mapbox/light-v11',
                center,
                zoom: parseFloat(unitMapNode.dataset.zoom || '12'),
                cooperativeGestures: true,
                attributionControl: false,
            });
            map.addControl(new mapboxgl.AttributionControl({ compact: true }));
            map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            unitMapNode.__map = map;

            // Tilpass kartet paletten: vann i brand2, land i sidens bakgrunn,
            // veier i ink (kantlinjer i landfargen så strekene blir rene)
            map.on('style.load', () => {
                const css = getComputedStyle(document.body);
                const waterColor = css.getPropertyValue('--color-brand2').trim() || '#AEC7C4';
                const landColor = css.getPropertyValue('--color-themebg').trim() || '#F7F3EC';
                const roadColor = css.getPropertyValue('--color-themedeactive').trim() || '#B6AA92';
                const roadMajorColor = css.getPropertyValue('--color-driftwood').trim() || '#7A6E57';
                const terrainShade = css.getPropertyValue('--color-themedeactive').trim() || '#B6AA92';

                map.getStyle().layers.forEach((layer) => {
                    if (layer.type === 'background') {
                        map.setPaintProperty(layer.id, 'background-color', landColor);
                    } else if (/^(landuse|national-park)$/.test(layer.id)) {
                        // Grå terreng-/arealfyll -> mørkere beige-valør (themedeactive tynnet mot land)
                        map.setPaintProperty(layer.id, 'fill-color', terrainShade);
                        map.setPaintProperty(layer.id, 'fill-opacity', 0.3);
                    } else if (/^(water|water-shadow|waterway)$/.test(layer.id)) {
                        map.setPaintProperty(layer.id, layer.type === 'line' ? 'line-color' : 'fill-color', waterColor);
                    } else if (layer.type === 'line' && /road|bridge|tunnel/.test(layer.id)) {
                        const isCasing = /case|casing/.test(layer.id);
                        // I light-stilen er kjøreveiene *-simple-lag; resten er stier/trapper
                        const isRoad = /-simple$/.test(layer.id);
                        map.setPaintProperty(layer.id, 'line-color', isCasing ? landColor : (isRoad ? roadMajorColor : roadColor));
                    }
                });
            });

            const pin = document.createElement('div');
            pin.className = 'unit-map-marker';
            pin.setAttribute('aria-label', unitMapNode.dataset.title || '');
            pin.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path fill="currentColor" stroke="none" d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3" fill="#F7F3EC" stroke="none"/></svg>';
            new mapboxgl.Marker({ element: pin, anchor: 'bottom' }).setLngLat(center).addTo(map);
        });
    }

    // Bildegalleri (rental unit) — PhotoSwipe-lightbox + rolig entrance fra høyre
    const galleryNode = document.querySelector('[data-gallery]');
    if (galleryNode) {
        const lightbox = new PhotoSwipeLightbox({
            gallery: '[data-gallery]',
            children: 'a.gallery-item',
            pswpModule: PhotoSwipe,
            showHideAnimationType: 'zoom',
        });
        lightbox.init();

        // «Flere bilder»-flisen åpner lightboxen på første skjulte bilde
        const moreBtn = galleryNode.querySelector('[data-gallery-more]');
        if (moreBtn) {
            moreBtn.addEventListener('click', () => {
                lightbox.loadAndOpen(parseInt(moreBtn.dataset.startIndex || '4', 10));
            });
        }

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            // Animer den indre wrapperen, ikke .gallery-item selv — elementene
            // er scroll-snap-mål, og transform på dem får snappingen til å hoppe
            gsap.from(galleryNode.querySelectorAll('.gallery-item > div'), {
                x: 90,
                opacity: 0,
                duration: 1,
                ease: 'power2.out',
                stagger: 0.1,
                scrollTrigger: {
                    trigger: galleryNode,
                    start: 'top 88%',
                    once: true,
                },
            });
        }
    }

    // Rolig innfading ved scroll. Opt-in med [data-reveal] slik at den bare
    // treffer der den er ønsket — et bredt selektor-sveip ville animert halve
    // siden. Legg til data-reveal-stagger for å forskyve barna i stedet.
    //
    // fromTo med immediateRender: false er et bevisst valg. gsap.from() skjuler
    // elementet i det øyeblikket koden kjører, og fyrer ScrollTrigger aldri —
    // fordi et script feilet, eller fordi fanen ikke rendres — blir innholdet
    // borte for godt. Her settes startverdiene først når triggeren løper, så
    // enhver feil ender med synlig innhold i stedet for en tom side.
    const START = 0.88;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.querySelectorAll('[data-reveal]').forEach((el) => {
            // Hopp over det som allerede er synlig ved lasting. Slike elementer
            // ville blinket — synlig, så skjult, så inn igjen — og de har
            // uansett ingen scroll å komme inn på.
            if (el.getBoundingClientRect().top < window.innerHeight * START) {
                return;
            }

            const targets = el.hasAttribute('data-reveal-stagger')
                ? el.children
                : el;

            gsap.fromTo(
                targets,
                { y: 14, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.7,
                    ease: 'power3.out',
                    stagger: 0.06,
                    immediateRender: false,
                    scrollTrigger: {
                        trigger: el,
                        start: `top ${START * 100}%`,
                        once: true,
                    },
                }
            );
        });
    }

    // Embla carousel — main
    const emblaNode = document.querySelector('.embla');
    if (emblaNode) {
        const embla = EmblaCarousel(emblaNode, {
            active: true,
            loop: false,
            align: 'start',
            dragFree: false,
            containScroll: 'trimSnaps', // Hindrer at man drar inn tomrom på slutten
            slides: 'li.embla__card',
        });

        const prevButtonNode = emblaNode.querySelector('.embla__prev');
        const nextButtonNode = emblaNode.querySelector('.embla__next');

        if (prevButtonNode) prevButtonNode.addEventListener('click', embla.scrollPrev, false);
        if (nextButtonNode) nextButtonNode.addEventListener('click', embla.scrollNext, true);

        console.log('✅ Embla Carousel is initialized:', embla);
        console.log('Slides:', embla.slideNodes().length);
        console.log('Snap points:', embla.scrollSnapList());
    }

    // Embla carousel — mina
    const minaSlides = document.querySelector('.mina');
    if (minaSlides) {
        const mina = EmblaCarousel(minaSlides, {
            active: true,
            loop: false,
            align: 'start',
            dragFree: false,
            containScroll: 'trimSnaps',
            slides: 'li.mina__slide',
        });

        const prevButtonNode = minaSlides.querySelector('.mina__prev');
        const nextButtonNode = minaSlides.querySelector('.mina__next');

        if (prevButtonNode) prevButtonNode.addEventListener('click', mina.scrollPrev, false);
        if (nextButtonNode) nextButtonNode.addEventListener('click', mina.scrollNext, true);

        const dotsNode = minaSlides.querySelector('.mina__dots');
        if (dotsNode) addDotBtnsAndClickHandlers(mina, dotsNode);
    }

});
